import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { GalleryService } from 'app/gallery/gallery.service';
import { ImagePlaceholder
       } from 'collage-designer/stage/placeholder/image-placeholder';
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { GalleryConstants } from 'core/constants';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { Subscription } from 'rxjs';
import { Utils } from '../../core/utils';
import { ToolboxComponent } from '../toolbox.component';
import { ToolboxService } from '../toolbox.service';

@Component({
    selector: 'gallery-toolbox',
    templateUrl: './gallery-toolbox.component.html',
    styleUrls: ['../toolbox.component.scss',],
    standalone: false
})
export class GalleryToolboxComponent extends ToolboxComponent
    implements OnDestroy, OnInit
{
  public placeholder?: ImagePlaceholder;
  public isPlacingImage: boolean = false;
  public showUploadButton: boolean = false;
  public isVisible: boolean = false;

  private placeholderChangedSubscription: Subscription;
  private uploadProgressChangedSubscription:
      AsyncEventEmitterSubscriber<boolean>;

  constructor(
      mir: MatIconRegistry,
      sanitizer: DomSanitizer,
      @Inject(ToolboxService) toolboxService,
      @Inject(GalleryService) private galleryService,
      @Inject(PlaceholderService) private placeholderService,
      @Inject(EventTrackingService) private eventTrackingService,
      @Inject(ProductService) private productService)
  {
    super(mir, sanitizer, toolboxService);

    this.loadIcon('photos');
    this.placeholder = <ImagePlaceholder> this.placeholder;

    this.placeholderChangedSubscription =
        this.toolboxService.placeholderChanged.subscribe((placeholder) => {
          if (placeholder instanceof ImagePlaceholder) {
            this.placeholder = <ImagePlaceholder> placeholder;
          }
          else if (placeholder === undefined) {
            this.placeholder = undefined;
          }
          this.isPlacingImage = false;
        });

    this.uploadProgressChangedSubscription =
        this.galleryService.uploadProgressChanged.subscribe((inProgress) => {
          if (!inProgress && GalleryConstants.CLOSE_GALLERY_AFTER_UPLOAD) {
            this.closeIfSmallOrMediumDevice();
          }
        });
  }

  public ngOnInit(): void
  {
    this.showUploadButton = this.shouldShowUploadButton();
    this.isVisible = this.shouldBeVisible();
  }

  public ngOnDestroy(): void
  {
    this.placeholderChangedSubscription.unsubscribe();
    this.galleryService.uploadProgressChanged.unsubscribe(
        this.uploadProgressChangedSubscription);
  }

  public btnOKClick(): void
  {
    super.btnOKClick();
    this.galleryService.setSelectedImageId(undefined);
    this.removeHighlighter(this.placeholderService.getImagePlaceholders(true));
  }

  public async changeImage(imageId: string): Promise<void>
  {
    if (this.placeholder) {
      await this.placeholder.loadGalleryImage(imageId);
      this.closeIfSmallOrMediumDevice();
    }
    else {
      const placeholders = this.placeholderService.getImagePlaceholders(true);

      if (placeholders.length === 1) {
        const placeholder = placeholders[0];
        await placeholder.loadGalleryImage(imageId);
        this.closeIfSmallOrMediumDevice();
      }
      else if (placeholders.length > 1) {
        this.placeholderService.disableTextPlaceholders();
        this.galleryService.setSelectedImageId(imageId);
        this.addHighlighter(placeholders);
      }
    }
  }

  private shouldShowUploadButton(): boolean
  {
    return Utils.isSmallDevice() || Utils.isMediumDevice();
  }

  private shouldBeVisible(): boolean
  {
    return !((Utils.isSmallDevice() || Utils.isMediumDevice()) &&
             this.isPlacingImage);
  }

  public trackUploadPhotos(): void
  {
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'init_photo_upload',
      label: 'gallery_upload_button',
    });
  }

  private addHighlighter(placeholders: Array<ImagePlaceholder>): void
  {
    for (let placeholder of placeholders) {
      placeholder.showPlaceholderHighlighter();
    }

    this.isPlacingImage = true;
    this.isVisible = this.shouldBeVisible();
  }

  private removeHighlighter(placeholders: Array<ImagePlaceholder>): void
  {
    for (let placeholder of placeholders) {
      placeholder.hidePlaceholderHighlighter();
    }

    this.placeholderService.enableTextPlaceholders();

    this.isPlacingImage = false;
    this.isVisible = this.shouldBeVisible();
  }

  private closeIfSmallOrMediumDevice(): void
  {
    if (Utils.isSmallDevice() || Utils.isMediumDevice()) {
      this.btnOKClick();
    }
  }
}
